import { Injectable, signal } from '@angular/core';

import { RepresentativeInput } from '../../../domain/representative/dtos/representative.dto';
import { AddressFormValue } from '../create-account/account-form.handler';

export interface CompanyInfo {
  razaoSocial: string;
  nomeFantasia: string;
  cnpj: string;
  telefone: string;
  email: string;
  emailConfirmacao: string;
}

export interface RfValidationResult {
  cpf: string;
  valid: boolean;
  relacao?: string;
  motivo?: string;
  documentUrl?: string;
  termAcceptedAt?: string;
}

@Injectable()
export class CreateAccountPjFormHandler {
  private _step = signal(0);
  private _companyInfo = signal<CompanyInfo | null>(null);
  private _addressInfo = signal<AddressFormValue | null>(null);
  private _representatives = signal<RepresentativeInput[]>([]);
  private _verifiedRepresentativeCpf = signal<string | null>(null);
  private _rfResults = signal<Map<string, RfValidationResult>>(new Map());
  private _password = signal<string>('');

  getStep(): number {
    return this._step();
  }

  setStep(step: number): void {
    this._step.set(step);
  }

  nextStep(): void {
    this._step.set(this._step() + 1);
  }

  backStep(): void {
    this._step.set(Math.max(0, this._step() - 1));
  }

  getCompanyInfo(): CompanyInfo | null {
    return this._companyInfo();
  }

  setCompanyInfo(data: CompanyInfo): void {
    this._companyInfo.set(data);
  }

  getAddressInfo(): AddressFormValue | null {
    return this._addressInfo();
  }

  setAddressInfo(data: AddressFormValue): void {
    this._addressInfo.set(data);
  }

  getRepresentatives(): RepresentativeInput[] {
    return this._representatives();
  }

  setRepresentatives(data: RepresentativeInput[]): void {
    this._representatives.set(data);
  }

  /** Quem entrou pelo gov.br abre a lista de representantes, e não pode ser trocado nem removido. */
  setVerifiedRepresentative({ cpf, nome, email }: RepresentativeInput): void {
    this._verifiedRepresentativeCpf.set(cpf.replace(/\D/g, ''));
    if (!this._representatives().length) {
      this._representatives.set([{ cpf, nome, email }]);
    }
  }

  isVerifiedRepresentative(cpf: string): boolean {
    return Boolean(cpf) && cpf.replace(/\D/g, '') === this._verifiedRepresentativeCpf();
  }

  getRfResults(): Map<string, RfValidationResult> {
    return this._rfResults();
  }

  setRfResult(cpf: string, result: RfValidationResult): void {
    const map = new Map(this._rfResults());
    map.set(cpf, result);
    this._rfResults.set(map);
  }

  getPassword(): string {
    return this._password();
  }

  setPassword(password: string): void {
    this._password.set(password);
  }

  allRepresentativesValidated(): boolean {
    const reps = this._representatives();
    const results = this._rfResults();
    return reps.every((r) => results.has(r.cpf));
  }
}
